package ai.jessie.web.profile

import ai.jessie.web.theme.ButtonVariant
import ai.jessie.web.theme.CardBodyStyle
import ai.jessie.web.theme.CardFootStyle
import ai.jessie.web.theme.CardHeadStyle
import ai.jessie.web.theme.CardStackStyle
import ai.jessie.web.theme.CardStyle
import ai.jessie.web.theme.FieldErrorStyle
import ai.jessie.web.theme.FieldInputStyle
import ai.jessie.web.theme.FieldOptionalStyle
import ai.jessie.web.theme.FieldStyle
import ai.jessie.web.theme.FieldsetStyle
import ai.jessie.web.theme.Grid2Style
import ai.jessie.web.theme.HintStyle
import ai.jessie.web.theme.LegendStyle
import ai.jessie.web.theme.MutedStyle
import ai.jessie.web.theme.SmallStyle
import ai.jessie.web.theme.SubListStyle
import ai.jessie.web.theme.TableStrongStyle
import ai.jessie.web.theme.themedButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import com.varabyte.kobweb.core.AppGlobals
import com.varabyte.kobweb.core.isExporting
import com.varabyte.kobweb.silk.style.toAttrs
import kotlinx.browser.document
import org.jetbrains.compose.web.attributes.AttrsScope
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.forId
import org.jetbrains.compose.web.attributes.selected
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Fieldset
import org.jetbrains.compose.web.dom.Form
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Label
import org.jetbrains.compose.web.dom.Legend
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Option
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Select
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul
import org.w3c.dom.HTMLElement

// The scopes as fieldsets shown as cards; the last card holds the single footer.
@Composable
internal fun profileForm(state: ProfileUiState, actions: ProfileActions, focusReturn: FocusReturn) {
    val draft = state.draft ?: return
    val membership = state.current?.membership
    val footer: @Composable () -> Unit = {
        themedButton("Abbrechen", actions.cancel, disabled = state.busy)
        themedButton(
            "Änderungen prüfen",
            {},
            ButtonVariant.DARK,
            disabled = state.busy,
            submit = true,
        )
    }
    Form(
        attrs = {
            addEventListener("submit") {
                it.preventDefault()
                actions.review()
            }
        }
    ) {
        Div(attrs = CardStackStyle.toAttrs()) {
            Div(attrs = CardStyle.toAttrs()) {
                Div(attrs = CardHeadStyle.toAttrs()) {
                    profileHeading("Profil bearbeiten", state, focusReturn)
                }
                scopeFields("Global – gilt in ganz Jessie", "global.", state, actions)
                if (draft.fields.keys.none { it.startsWith("membership.") })
                    Div(attrs = CardBodyStyle.toAttrs()) {
                        P(attrs = HintStyle.toAttrs()) {
                            Text("Ohne ausgewählte Organisation sind nur globale Angaben änderbar.")
                        }
                    }
                if (membership == null) Div(attrs = CardFootStyle.toAttrs()) { footer() }
            }
            membership?.let {
                Div(attrs = CardStyle.toAttrs()) {
                    scopeFields(
                        "Nur in der Organisation «${it.organisationName}»",
                        "membership.",
                        state,
                        actions,
                    )
                    Div(attrs = CardFootStyle.toAttrs()) { footer() }
                }
            }
        }
    }
}

// Two fields per row, as on the artboard (Vorname · Nachname).
@Composable
private fun scopeFields(
    legend: String,
    scope: String,
    state: ProfileUiState,
    actions: ProfileActions,
) {
    Fieldset(attrs = FieldsetStyle.toAttrs { if (state.busy) attr("disabled", "") }) {
        Legend(attrs = LegendStyle.toAttrs()) { Text(legend) }
        Div(attrs = CardBodyStyle.toAttrs()) {
            state.draft
                ?.fields
                ?.filterKeys { it.startsWith(scope) }
                ?.toList()
                ?.chunked(2)
                ?.forEach { row ->
                    Div(attrs = Grid2Style.toAttrs()) {
                        row.forEach { (name, value) -> profileControl(name, value, state, actions) }
                    }
                }
            if (scope == "membership.")
                Div(attrs = listOf(SmallStyle, MutedStyle).toAttrs()) {
                    Text("Leerer Anzeigename: es gilt dein globaler Anzeigename.")
                }
        }
    }
}

@Composable
private fun profileControl(
    name: String,
    value: String,
    state: ProfileUiState,
    actions: ProfileActions,
) {
    val problem = state.problems[name]
    when (name) {
        LOCALE -> profileSelect(name, value, mapOf("de" to "Deutsch"), problem, actions)
        COMMENT_EMAIL ->
            profileSelect(
                name,
                value,
                mapOf("true" to "Aktiviert", "false" to "Deaktiviert"),
                problem,
                actions,
            )
        else -> profileInput(name, value, problem, actions)
    }
}

@Composable
private fun profileInput(name: String, value: String, problem: String?, actions: ProfileActions) {
    fieldBlock(name, problem) {
        Input(
            InputType.Text,
            attrs =
                FieldInputStyle.toAttrs {
                    value(value)
                    fieldAttributes(name, problem)
                    onInput { actions.change(name, it.value) }
                },
        )
    }
}

@Composable
private fun profileSelect(
    name: String,
    value: String,
    options: Map<String, String>,
    problem: String?,
    actions: ProfileActions,
) {
    fieldBlock(name, problem) {
        Select(
            attrs =
                FieldInputStyle.toAttrs {
                    fieldAttributes(name, problem)
                    onChange { actions.change(name, it.value.orEmpty()) }
                }
        ) {
            options.forEach { (option, label) ->
                Option(option, attrs = { if (option == value) selected() }) { Text(label) }
            }
        }
    }
}

// The label text is unchanged; its trailing scope parenthesis is rendered in the quieter suffix
// style (`SettingsField.kt`).
@Composable
private fun fieldBlock(name: String, problem: String?, control: @Composable () -> Unit) {
    Div(attrs = FieldStyle.toAttrs()) {
        Label(attrs = { forId("profile-$name") }) {
            val label = fieldLabel(name)
            val hint = label.indexOf(" (")
            if (hint < 0) Text(label)
            else {
                Text(label.substring(0, hint + 1))
                Span(attrs = FieldOptionalStyle.toAttrs()) { Text(label.substring(hint + 1)) }
            }
        }
        control()
        problem?.let { P(attrs = FieldErrorStyle.toAttrs()) { Text(it) } }
    }
}

private fun AttrsScope<*>.fieldAttributes(name: String, problem: String?) {
    id("profile-$name")
    attr("name", name)
    attr("autocomplete", "off")
    if (problem != null) {
        attr("aria-invalid", "true")
        attr("aria-describedby", PROFILE_PROBLEMS_ID)
    }
}

// Steps 5–6: the server's exact changes, each labelled with its scope. Nothing is saved until the
// user confirms; cancelling (A7) sends nothing.
@Composable
internal fun profileReview(state: ProfileUiState, actions: ProfileActions, focus: FocusReturn) {
    val changes = state.review?.changes.orEmpty()
    Div(
        attrs =
            CardStyle.toAttrs {
                id(REVIEW_ID)
                attr("role", "alertdialog")
                attr("aria-labelledby", "profile-review-title")
                attr("tabindex", "-1")
            }
    ) {
        Div(attrs = CardHeadStyle.toAttrs()) {
            H2(attrs = { id("profile-review-title") }) { Text("Änderungen prüfen und bestätigen") }
        }
        Div(attrs = CardBodyStyle.toAttrs()) {
            if (changes.isEmpty()) {
                P(attrs = HintStyle.toAttrs()) {
                    Text("Der Vorschlag ändert nichts. Es gibt nichts zu speichern.")
                }
            } else {
                Ul(attrs = SubListStyle.toAttrs()) {
                    changes.forEach {
                        val field = "${it.scope.lowercase()}.${it.field}"
                        Li {
                            Text(
                                "${changeLabel(it.scope, it.field)}: ${shown(field, it.previous)} → "
                            )
                            Span(attrs = TableStrongStyle.toAttrs()) { Text(shown(field, it.new)) }
                        }
                    }
                }
            }
        }
        Div(attrs = CardFootStyle.toAttrs()) {
            themedButton("Abbrechen", actions.cancel, ButtonVariant.GHOST, disabled = state.busy)
            themedButton("Zurück zur Bearbeitung", actions.revise, disabled = state.busy)
            if (changes.isNotEmpty())
                themedButton(
                    "Änderungen speichern",
                    actions.confirm,
                    ButtonVariant.PRIMARY,
                    disabled = state.busy,
                )
        }
    }
    LaunchedEffect(state.review) {
        focus.pending = true
        if (!AppGlobals.isExporting) (document.getElementById(REVIEW_ID) as? HTMLElement)?.focus()
    }
}

private const val REVIEW_ID = "profile-review"
